import { FocusableComponentLayout, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import {
  ContentScrollingContentAxis,
  FocusableItem,
  RenderItemType,
  StripeItemData
} from 'common/interfaces';
import { getClassName } from 'common/utils';
import { useHoverable } from 'common/utils/hooks';
import React, { MouseEvent, ReactElement, useEffect, useRef } from 'react';
import './Stripe.styles';

interface StripeItemProps extends FocusableItem {
  index: number;
  // Used to track the index of the stripes in a list which are focusable
  focusableIndex: number;
  fastScroll?: boolean;
  axis?: ContentScrollingContentAxis;
  first?: boolean;
  last?: boolean;
  children?: ReactElement<{ focused?: boolean; hidden?: boolean }>;
  hidden?: boolean;
  getFocusKey?: (index: number, focusKey: string) => void;
  renderItem: RenderItemType<unknown>;
  item: StripeItemData;
}

function StripeItem({
  index,
  focusableIndex,
  children,
  onEnterPress,
  onFocus,
  onBlur,
  onArrowPress,
  onClick: onClickParam,
  onHover: onHoverParam,
  onMouseLeave: onMouseLeaveParam,
  focusKey: focusKeyParam,
  axis,
  stripeItemClassName,
  disabled,
  hidden,
  getFocusKey,
  renderItem,
  item
}: StripeItemProps) {
  const {
    ref,
    focused: directionalFocused,
    focusSelf,
    focusKey
  } = useFocusable({
    focusable: !disabled,
    onEnterPress,
    onFocus,
    onBlur,
    onArrowPress,
    focusKey: focusKeyParam,
    extraProps: {
      index
    }
  });
  const { active: focused, onMouseEnter, onMouseLeave } = useHoverable(directionalFocused);
  const getFocusKeyRef = useRef(getFocusKey);

  useEffect(() => {
    getFocusKeyRef.current = getFocusKey;
  }, [getFocusKey]);

  useEffect(() => {
    getFocusKeyRef.current && getFocusKeyRef.current(focusableIndex, focusKey);
    return () => {
      getFocusKeyRef.current && getFocusKeyRef.current(-1, focusKey);
    };
  }, [focusKey, focusableIndex]);

  useEffect(() => {
    // Sync focus
    if (focused && !directionalFocused) focusSelf();
  }, [focused, directionalFocused]);

  const onClick = () => {
    onClickParam && onClickParam({ index });
  };

  const onMouseEnterCallback = (e: MouseEvent<HTMLDivElement>) => {
    onMouseEnter();
    const layout: FocusableComponentLayout = {
      left: e.clientX,
      top: e.clientY,
      width: e.currentTarget.offsetWidth,
      height: e.currentTarget.offsetHeight,
      right: e.clientX + e.currentTarget.offsetWidth,
      bottom: e.clientY + e.currentTarget.offsetHeight,
      x: e.clientX,
      y: e.clientY,
      node: e.currentTarget
    };
    const data = { index };
    onHoverParam && onHoverParam(layout, data);
  };

  const onMouseLeaveCallback = () => {
    onMouseLeave();
    onMouseLeaveParam && onMouseLeaveParam();
  };

  const renderChild = () => {
    const children = renderItem(item, index) as unknown as ReactElement<{
      focused?: boolean;
      hidden?: boolean;
    }>;
    return children && React.isValidElement(children)
      ? React.cloneElement(children, { focused, hidden })
      : children;
  };

  return (
    <div
      ref={ref}
      onClick={onClick}
      onMouseMove={onMouseEnterCallback}
      onMouseLeave={onMouseLeaveCallback}
      tabIndex={-1}
      className={getClassName(`stripe-item ${axis || 'x'} ${stripeItemClassName || ''}`, {
        focused,
        first: index === 0
      })}
    >
      {renderChild()}
    </div>
  );
}

// Ignore children change
export default React.memo(StripeItem);
